import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  FileText,
  LockKeyhole,
  Mail,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import './LegalPages.css';

interface LegalSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface LegalPageProps {
  type: 'privacy' | 'terms';
  eyebrow: string;
  title: string;
  summary: string;
  sections: LegalSection[];
}

const EffectiveDate = () => (
  <div className="legal-date-card">
    <span>Effective date</span>
    <strong>15 August 2026</strong>
    <small>Last updated on the same date</small>
  </div>
);

const LegalPage: React.FC<LegalPageProps> = ({ type, eyebrow, title, summary, sections }) => {
  const HeaderIcon = type === 'privacy' ? ShieldCheck : Scale;

  return (
    <div className="legal-page">
      <div className="legal-page__glow" aria-hidden="true" />
      <div className="legal-shell">
        <header className="legal-hero">
          <div className="legal-hero__copy">
            <span className="legal-eyebrow"><HeaderIcon size={14} /> {eyebrow}</span>
            <h1>{title}</h1>
            <p>{summary}</p>
          </div>
          <EffectiveDate />
        </header>

        <div className="legal-layout">
          <aside className="legal-sidebar" aria-label={`${title} contents`}>
            <div className="legal-sidebar__title"><FileText size={16} /> On this page</div>
            <nav>
              {sections.map((section, index) => (
                <a key={section.id} href={`#${section.id}`}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {section.title}
                </a>
              ))}
            </nav>
            <div className="legal-sidebar__help">
              <Mail size={16} />
              <div><strong>Have a question?</strong><Link to="/contact">Contact the team</Link></div>
            </div>
          </aside>

          <article className="legal-document">
            <div className="legal-summary-note">
              <LockKeyhole size={19} />
              <p>
                We use clear language here so you can understand the practical effect of these terms.
                Section headings help navigation but do not limit the text beneath them.
              </p>
            </div>

            {sections.map((section, index) => (
              <section id={section.id} key={section.id} className="legal-section">
                <div className="legal-section__heading">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h2>{section.title}</h2>
                </div>
                <div className="legal-section__body">{section.content}</div>
              </section>
            ))}

            <div className="legal-end-card">
              <CheckCircle2 size={20} />
              <div>
                <strong>Questions about this document?</strong>
                <p>Use the Contact page and select “Account help” or “Something else.”</p>
              </div>
              <Link to="/contact">Contact us <ArrowUpRight size={15} /></Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

const privacySections: LegalSection[] = [
  {
    id: 'privacy-scope',
    title: 'Scope and our role',
    content: <>
      <p>This Privacy Policy explains how LetsBoostUp collects, uses, stores, and shares personal data when you use our website, learning content, quizzes, coding playgrounds, practice workspaces, account features, and support channels.</p>
      <p>For personal data processed to operate LetsBoostUp, the platform operator acts as the data fiduciary or controller. This policy is designed to provide a clear notice consistent with applicable privacy law, including India’s Digital Personal Data Protection framework.</p>
    </>,
  },
  {
    id: 'privacy-data',
    title: 'Data we collect',
    content: <>
      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account information:</strong> name, email address, profile image, authentication details, and account preferences.</li>
        <li><strong>Learning activity:</strong> lesson completion, quiz attempts, ratings, solved problems, streaks, and practice progress.</li>
        <li><strong>User content:</strong> comments, replies, code, saved machine-coding projects, LLD workspace entries, and other material you choose to save.</li>
        <li><strong>Support information:</strong> contact category, subject, message, optional page URL, and the email address used for our response.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <p>We may process basic device, browser, IP address, request, error, and security-log information needed to deliver the service, diagnose failures, prevent abuse, and protect accounts. Some playground features run entirely inside your browser; others may communicate with LetsBoostUp services when saving progress or executing supported workloads.</p>
    </>,
  },
  {
    id: 'privacy-use',
    title: 'How we use data',
    content: <>
      <p>We process personal data only for defined platform purposes:</p>
      <ul>
        <li>creating and securing accounts, including Google sign-in when selected;</li>
        <li>delivering lessons, practice environments, quizzes, saved work, and progress tracking;</li>
        <li>displaying contributions such as comments and ratings;</li>
        <li>responding to support, correction, feedback, and partnership messages;</li>
        <li>maintaining reliability, measuring feature performance, debugging, and preventing misuse;</li>
        <li>meeting legal obligations and enforcing platform terms.</li>
      </ul>
      <p>Where consent is the applicable basis, you may withdraw it. Withdrawal does not affect processing that was lawful before it was withdrawn or processing required for another lawful purpose.</p>
    </>,
  },
  {
    id: 'privacy-sharing',
    title: 'Service providers and sharing',
    content: <>
      <p>We do not sell personal data. We share limited data only when necessary to operate the platform, comply with law, protect users, or complete a transaction you request.</p>
      <p>Current service categories may include cloud hosting, database infrastructure, Google authentication, Gmail email delivery through Nodemailer, source/deployment infrastructure, and security or diagnostic services. These providers process data under their own contractual and security responsibilities. If the business is reorganised, relevant data may transfer subject to appropriate safeguards and notice.</p>
    </>,
  },
  {
    id: 'privacy-storage',
    title: 'Cookies and local storage',
    content: <>
      <p>LetsBoostUp uses browser storage and similar essential technologies to keep you signed in, remember interface settings, preserve local playground state, and support application security. Some coding runtimes and learning tools may store data locally in your browser.</p>
      <p>Clearing browser data can sign you out or remove locally stored work that has not been synchronised. We do not currently describe an advertising-cookie programme because the platform does not use one.</p>
    </>,
  },
  {
    id: 'privacy-retention',
    title: 'Retention and deletion',
    content: <>
      <p>We retain account and learning data while your account remains active and for a reasonable period afterward when needed for restoration, security, dispute handling, or legal compliance. Contact messages are kept only as long as reasonably required to resolve the enquiry and maintain necessary support records.</p>
      <p>Retention can vary by data type. Backup copies may remain for a limited period after deletion and are removed through normal backup rotation. We may retain de-identified or aggregated information that no longer identifies you.</p>
    </>,
  },
  {
    id: 'privacy-security',
    title: 'Security',
    content: <>
      <p>We apply reasonable technical and organisational safeguards, including access controls, authenticated APIs, transport security, input validation, restricted credentials, and abuse controls. No online system can guarantee absolute security, so you should use a unique password and protect access to your email and devices.</p>
      <p>Do not place passwords, access tokens, private keys, production secrets, personal financial data, or confidential employer code into comments, contact messages, or practice editors.</p>
    </>,
  },
  {
    id: 'privacy-rights',
    title: 'Your choices and rights',
    content: <>
      <p>Subject to applicable law, you may request access to information about your data, correction of inaccurate data, erasure, withdrawal of consent, or grievance resolution. You may also sign out, delete local browser data, stop optional contributions, or request account assistance.</p>
      <p>Submit a request through the <Link to="/contact">Contact page</Link>. We may need to verify your identity before acting. If you submit a grievance, please provide enough detail for us to locate the relevant account or activity without sharing passwords or authentication codes.</p>
    </>,
  },
  {
    id: 'privacy-children',
    title: 'Children and younger users',
    content: <>
      <p>Users who are under 18, or below the age at which they may independently consent under applicable law, should use LetsBoostUp only with the involvement and consent of a parent or lawful guardian. We do not knowingly seek unnecessary personal data from children or use behavioural advertising directed at them.</p>
      <p>If you believe a child provided personal data without appropriate authorisation, contact us so we can investigate and take suitable action.</p>
    </>,
  },
  {
    id: 'privacy-changes',
    title: 'Changes and contact',
    content: <>
      <p>We may update this policy when platform features, providers, or legal requirements change. The updated date will appear at the top. Material changes may also be communicated through the platform or the email associated with your account when appropriate.</p>
      <p>For privacy questions, requests, or grievances, use our <Link to="/contact">Contact page</Link> and include “Privacy request” in the subject.</p>
    </>,
  },
];

const termsSections: LegalSection[] = [
  {
    id: 'terms-acceptance',
    title: 'Acceptance and eligibility',
    content: <>
      <p>These Terms of Service govern your use of LetsBoostUp. By creating an account or using the platform, you agree to these Terms and acknowledge the Privacy Policy. If you do not agree, do not use the service.</p>
      <p>You must be legally capable of entering this agreement. Users under 18 or below the applicable age of independent consent may use the platform only with permission and supervision from a parent or lawful guardian.</p>
    </>,
  },
  {
    id: 'terms-service',
    title: 'The learning service',
    content: <>
      <p>LetsBoostUp provides educational material and practice tools for software-engineering skills and interview preparation, including articles, quizzes, coding playgrounds, DSA problems, machine-coding projects, and system-design or LLD workspaces.</p>
      <p>Features, content, limits, and availability may change as the platform evolves. We may correct, reorganise, add, suspend, or remove material when reasonably necessary. We do not guarantee that completing content will result in employment, interview success, certification, or any particular professional outcome.</p>
    </>,
  },
  {
    id: 'terms-accounts',
    title: 'Accounts and security',
    content: <>
      <p>You are responsible for information submitted through your account and for keeping your sign-in methods secure. Provide accurate information, do not impersonate another person, and notify us if you reasonably believe your account has been compromised.</p>
      <p>You may not sell, transfer, share, or provide automated access to your account. Activity performed through your authenticated account may be treated as authorised by you unless you promptly report misuse.</p>
    </>,
  },
  {
    id: 'terms-use',
    title: 'Acceptable use',
    content: <>
      <p>You may use LetsBoostUp for personal learning and legitimate interview preparation. You must not:</p>
      <ul>
        <li>break applicable law or violate another person’s rights;</li>
        <li>probe, bypass, disable, or interfere with security, authentication, rate limits, or access controls;</li>
        <li>introduce malware, destructive code, automated scraping, excessive traffic, or unauthorised bots;</li>
        <li>use coding environments to attack systems, mine cryptocurrency, send spam, or access data without permission;</li>
        <li>post unlawful, deceptive, abusive, discriminatory, infringing, or confidential material;</li>
        <li>copy, resell, republish, or commercially exploit substantial platform content without written permission.</li>
      </ul>
    </>,
  },
  {
    id: 'terms-code',
    title: 'Code, playgrounds, and saved work',
    content: <>
      <p>Code playgrounds and workspaces are learning environments, not production hosting services. Execution may be limited, isolated, interrupted, reset, or unavailable. Do not rely on them for production workloads or the only copy of important work.</p>
      <p>You are responsible for code and dependencies you run. Never include real credentials, private keys, customer information, confidential employer code, or data you are not authorised to process. Download or separately back up work you cannot afford to lose.</p>
    </>,
  },
  {
    id: 'terms-content',
    title: 'Content and intellectual property',
    content: <>
      <p>LetsBoostUp and its original lessons, interface, branding, diagrams, problem structures, and software are protected by applicable intellectual-property law. We grant you a limited, personal, non-exclusive, non-transferable, revocable licence to access them for lawful learning use.</p>
      <p>You retain ownership of original material you submit. By posting content where other users can see it, you grant LetsBoostUp a worldwide, non-exclusive, royalty-free licence to host, reproduce, format, display, and moderate that material solely to operate, secure, and improve the platform. You confirm that you have the necessary rights to submit it.</p>
    </>,
  },
  {
    id: 'terms-education',
    title: 'Educational disclaimer',
    content: <>
      <p>Platform content is provided for education and practice. Technical guidance may become outdated, contain errors, or require adaptation to a particular system. Examples simplify real engineering decisions and do not replace professional security, legal, financial, employment, or architecture advice.</p>
      <p>You should verify critical information and exercise independent judgement before applying examples to production systems, interviews, workplace assignments, or assessments.</p>
    </>,
  },
  {
    id: 'terms-third-party',
    title: 'Third-party services and links',
    content: <>
      <p>The platform may depend on or link to third-party authentication, hosting, database, email, package, documentation, or development services. Their availability and terms are controlled by their respective providers. A link or integration does not imply endorsement of all third-party content.</p>
      <p>You are responsible for reviewing third-party terms before using an external service or package.</p>
    </>,
  },
  {
    id: 'terms-enforcement',
    title: 'Suspension and termination',
    content: <>
      <p>We may restrict or suspend access when reasonably necessary to investigate security threats, prevent harm, comply with law, maintain the service, or respond to a material breach of these Terms. Where appropriate, we will consider the nature and seriousness of the issue.</p>
      <p>You may stop using LetsBoostUp at any time and may request account assistance through the Contact page. Provisions that by their nature should survive termination—including intellectual property, disclaimers, liability limits, and dispute terms—will continue to apply.</p>
    </>,
  },
  {
    id: 'terms-liability',
    title: 'Availability and liability',
    content: <>
      <p>The service is provided on an “as available” basis to the extent permitted by law. We do not promise uninterrupted access, error-free content, permanent storage, compatibility with every device, or a specific interview or career result.</p>
      <p>To the maximum extent permitted by applicable law, LetsBoostUp will not be liable for indirect, incidental, special, consequential, or punitive loss, or for loss of data, opportunity, revenue, or reputation arising from use of or inability to use the service. Nothing in these Terms excludes rights or liability that cannot lawfully be excluded.</p>
    </>,
  },
  {
    id: 'terms-law',
    title: 'Governing law and changes',
    content: <>
      <p>These Terms are governed by the laws of India, without prejudice to mandatory rights that apply in your location. The parties should first attempt to resolve concerns in good faith through the Contact page before pursuing formal remedies.</p>
      <p>We may update these Terms to reflect platform, risk, or legal changes. The effective date will be updated, and material changes may be communicated through the service or by email when appropriate. Continued use after updated Terms take effect constitutes acceptance to the extent permitted by law.</p>
    </>,
  },
  {
    id: 'terms-contact',
    title: 'Contact',
    content: <>
      <p>Questions, content concerns, account issues, and legal notices may be submitted through the <Link to="/contact">LetsBoostUp Contact page</Link>. Select the closest enquiry category and include enough information for us to identify and address the issue.</p>
    </>,
  },
];

export const PrivacyPolicy: React.FC = () => (
  <LegalPage
    type="privacy"
    eyebrow="Your data, explained clearly"
    title="Privacy Policy"
    summary="What LetsBoostUp collects, why we use it, who supports our infrastructure, and the choices available to you."
    sections={privacySections}
  />
);

export const TermsOfService: React.FC = () => (
  <LegalPage
    type="terms"
    eyebrow="Rules for using the platform"
    title="Terms of Service"
    summary="The practical agreement governing accounts, learning content, playgrounds, saved work, contributions, and acceptable use."
    sections={termsSections}
  />
);
